import React from 'react'

import './Advisor.scss'

import Badge from 'react-bootstrap/Badge'
import ButtonGroup from 'react-bootstrap/ButtonGroup'
import ButtonToolbar from 'react-bootstrap/ButtonToolbar'
import Card from 'react-bootstrap/Card'
import Carousel from 'react-bootstrap/Carousel'
import Col from 'react-bootstrap/Col'
import Container from 'react-bootstrap/Container'
import Navbar from 'react-bootstrap/Navbar'
import Nav from 'react-bootstrap/Nav'
import Row from 'react-bootstrap/Row'
import ToggleButton from 'react-bootstrap/ToggleButton'

import { questions } from './questions'

import { Areas } from './models/Area'
import { IAnswer } from './models/IAnswer'
import { IQuestion } from './models/IQuestion'
import {
  Extension,
  Extensions,
  ExtensionType,
  IExtension,
} from './models/Extensions'

export interface IAdvisorProps {}

const Advisor: React.FC<IAdvisorProps> = (props: IAdvisorProps) => {
  const [carouselIndex, setCarouselIndex] = React.useState<number>(0)
  const [answers, setAnswers] = React.useState<IAnswer[]>([])
  const [advise, setAdvise] = React.useState<IExtension[]>([])

  /**
   * When the user clicks to answer a question.
   *
   * @param idx
   * @param required
   */
  const answer = (idx: number, required: boolean): void => {
    // Continue (slide) to the next question
    const keyToFind = required
      ? questions[idx].gotoAfter
      : questions[idx].skipTo
    let gotoIdx = questions.findIndex((q) => q.key === keyToFind)

    // Jump passed questions that should be skipped
    while (true && gotoIdx > -1) {
      if (
        'skipIf' in questions[gotoIdx] &&
        answers.some(
          (a) =>
            a.required &&
            questions[gotoIdx].skipIf.indexOf(questions[a.questionId].key) > -1
        )
      ) {
        gotoIdx = questions.findIndex(
          (q) => q.key === questions[gotoIdx].skipTo
        )
      } else {
        break
      }
    }

    if (gotoIdx > -1) {
      setCarouselIndex(gotoIdx)
    }

    // Remember the user's answer
    const _answers = [...answers]
    const existingAnswerIdx = _answers.findIndex((a) => a.questionId === idx)

    if (existingAnswerIdx === -1) {
      _answers.push({ questionId: idx, required })
    } else {
      _answers[existingAnswerIdx] = { questionId: idx, required }
    }

    // Check for dependencies and update if necessary
    if (!required) {
      questions.forEach((q: IQuestion, _idx: number) => {
        if (q.dependsOn && q.dependsOn === questions[idx].key) {
          for (let j = 0; j < _answers.length; j++) {
            if (_answers[j].questionId === _idx) {
              _answers[j].required = false
            }
          }
        }
      })
    }

    setAnswers(_answers)
  }

  /**
   * When the user clicks in the area menu
   *
   * @param area
   */
  const jump = (area: string): void => {
    const gotoIdx = questions.findIndex(
      (q) =>
        q.area.toString() === area &&
        answers.some((a) => questions[a.questionId].area === area)
    )

    if (gotoIdx > -1) {
      setCarouselIndex(gotoIdx)
    }
  }

  // Control the carousel-index elsewhere and prevent auto-scroll
  const handleSelect = (idx: number) => {}

  /**
   * Updates the advisor's recommendation
   */

  React.useEffect(() => {
    const result: IExtension[] = []
    const isRequired = (extension: Extension): boolean => {
      return answers.some(
        (a) => a.required && questions[a.questionId].extension === extension
      )
    }

    Extensions.forEach((e) => {
      // Add LOGIN but only if an addon / bundle is recommended and GRAPH MAILER is not already in the mix
      if (
        e.extension === Extension.wpo365Login &&
        result.length > 0 &&
        !result.some((r) => r.extension === Extension.wpo365MsGraphMailer)
      ) {
        result.push(e)
        return
      }

      if (isRequired(e.extension)) {
        // Avoid duplicates
        if (result.some((r) => r.extension === e.extension)) {
          return
        }

        // Add GRAPH MAILER MAILER but only if LOGIN is not required
        if (e.extension === Extension.wpo365MsGraphMailer) {
          if (
            result.length === 0 ||
            (result.length === 1 &&
              result.some((r) => r.extension === Extension.wpo365Mail) &&
              !isRequired(Extension.wpo365Login))
          ) {
            result.push(e)
          }
          return
        }

        // Do not recommend PROFILE+ if any other premium addon / bundle is required
        if (
          e.extension === Extension.wpo365LoginPlus &&
          result.some(
            (r) =>
              r.type === ExtensionType.addon || r.type === ExtensionType.bundle
          )
        ) {
          return
        }

        // Do not recommend any premium addon or bundle if INTEGRATE or CUSTOMERS is required
        if (
          (e.type === ExtensionType.addon || e.type === ExtensionType.bundle) &&
          result.some(
            (r) =>
              r.extension === Extension.wpo365Integrate ||
              r.extension === Extension.wpo365Customers
          )
        ) {
          return
        }

        // Do not recommend any addon if PROFESSIONAL is required
        if (
          e.type === ExtensionType.addon &&
          e.extension !== Extension.wpo365Scim &&
          e.extension !== Extension.wpo365Apps &&
          result.some((r) => r.extension === Extension.wpo365Pro)
        ) {
          return
        }

        result.push(e)
      }
    })

    // Bail out before replacing recommendation with a bundle if CUSTOMERS is required
    if (result.some((r) => r.extension === Extension.wpo365Customers)) {
      setAdvise(result.reverse())
      return
    }

    // Replace recommendation with a bundle if possible
    if (!result.some((r) => r.extension === Extension.wpo365Integrate)) {
      // Update to PROFESSIONAL
      const requiresScim = result.some(
        (r) => r.extension === Extension.wpo365Scim
      )
      const requiresApps = result.some(
        (r) => r.extension === Extension.wpo365Apps
      )
      const requiresPro = result.some(
        (r) => r.extension === Extension.wpo365Pro
      )
      const wpoPro = Extensions.find((e) => e.extension === Extension.wpo365Pro)
      const wpoIntegrate = Extensions.find(
        (e) => e.extension === Extension.wpo365Integrate
      )
      const wpoLogin = Extensions.find(
        (e) => e.extension === Extension.wpo365Login
      )

      if (requiresPro && (requiresApps || requiresScim)) {
        result.length = 0
        if (wpoIntegrate && wpoLogin) {
          result.push(wpoLogin)
          result.push(wpoIntegrate)
        }

        setAdvise(result)
        return
      }

      let totalCost = 0

      result.forEach((r) => {
        if (r.type === ExtensionType.addon) {
          totalCost += r.price
        }
      })

      let replaceWithBundle =
        totalCost >= 340
          ? wpoIntegrate
          : totalCost > 240 && !requiresScim && !requiresApps
          ? wpoPro
          : undefined

      if (!!replaceWithBundle) {
        result.length = 0
        if (replaceWithBundle && wpoLogin) {
          result.push(wpoLogin)
          result.push(replaceWithBundle)
        }
        setAdvise(result)
        return
      }
    }

    setAdvise(result.reverse())
  }, [answers])

  return (
    <Container>
      <Row>
        <Col>
          <Navbar expand='lg' className='bg-light'>
            <Container fluid>
              <Navbar.Toggle aria-controls='navbarScroll' />
              <Navbar.Collapse id='navbarScroll'>
                <Nav
                  className='m-auto'
                  style={{ maxHeight: '100px' }}
                  navbarScroll>
                  {Object.keys(Areas).map((k: string, idx: number) => (
                    <Nav.Item key={`nav-${k}`}>
                      <Nav.Link
                        className='text-weight-bold'
                        active={answers.some(
                          (a) => questions[a.questionId].area.toString() === k
                        )}
                        onClick={(e) => jump(k)}>
                        {Areas[k].name}
                      </Nav.Link>
                    </Nav.Item>
                  ))}
                </Nav>
              </Navbar.Collapse>
            </Container>
          </Navbar>
        </Col>
      </Row>
      <Row>
        <Col className='m-auto'>
          <Carousel
            data-bs-theme='dark'
            activeIndex={carouselIndex}
            onSelect={handleSelect}
            data-bs-interval='false'
            data-bs-ride='carousel'
            controls={false}>
            {questions.map((q: IQuestion, idx) => (
              <Carousel.Item key={`div-carousel-item-${idx}`}>
                <div
                  className='d-flex justify-content-center py-5'
                  style={{ minHeight: '400px' }}>
                  <Card
                    bg='light'
                    text='dark'
                    className='d-flex flex-column justify-content-between px-lg-5 w-100'>
                    <Card.Img
                      variant='top'
                      width={80}
                      height='auto'
                      src='https://www.wpo365.com/wp-content/uploads/2021/07/icon-128x128-1.png'
                      className='align-self-center pt-3'
                      style={{ width: '80px' }}
                    />
                    <Card.Body>
                      <hr style={{ margin: '0 0 1rem 0' }} />
                      <Card.Title className='text-center text-black'>
                        {`${q.title} (${idx + 1}/${questions.length})`}
                      </Card.Title>
                      {carouselIndex < questions.length - 1 ? (
                        <>
                          <Card.Text
                            className='text-center py-3'
                            dangerouslySetInnerHTML={{
                              __html: q.innerHtml,
                            }}></Card.Text>
                          {q.badges && (
                            <Card.Text className='text-center lead'>
                              {q.badges.map((b: string, _idx: number) => (
                                <>
                                  <Badge
                                    key={`badge-${idx}-${_idx}`}
                                    bg='secondary'
                                    style={{ borderRadius: '5px' }}>
                                    {b}
                                  </Badge>{' '}
                                </>
                              ))}
                            </Card.Text>
                          )}
                          <ButtonToolbar className='justify-content-center'>
                            <ButtonGroup>
                              <ToggleButton
                                key={q.key}
                                id={`${q.key}-0`}
                                type='radio'
                                variant={'outline-primary'}
                                className='wpo-advisor-btn'
                                name={q.key}
                                value={'yes'}
                                checked={answers.some(
                                  (a) => a.questionId === idx && a.required
                                )}
                                onClick={(e) => answer(idx, true)}>
                                Yes
                              </ToggleButton>
                              <ToggleButton
                                key={'1'}
                                id={`${q.key}-1`}
                                type='radio'
                                variant={'outline-primary'}
                                className='wpo-advisor-btn'
                                name={q.key}
                                value={'no'}
                                checked={answers.some(
                                  (a) => a.questionId === idx && !a.required
                                )}
                                onClick={(e) => answer(idx, false)}>
                                No
                              </ToggleButton>
                            </ButtonGroup>
                          </ButtonToolbar>
                        </>
                      ) : (
                        <>
                          <div className='text-center p-4'>
                            {advise.map((a: IExtension, idx: number) => (
                              <span key={`advise-${idx}`}>
                                <a
                                  href={a.url}
                                  onClick={() => {
                                    ;(window as any).dataLayer =
                                      (window as any).dataLayer || []
                                    ;(window as any).dataLayer.push({
                                      event: 'advisorLink',
                                      product: a.name,
                                    })
                                  }}
                                  target='_blank'
                                  rel='noreferrer'>
                                  <Badge
                                    bg={
                                      a.type === ExtensionType.free
                                        ? 'secondary'
                                        : a.type === ExtensionType.addon
                                        ? 'primary'
                                        : 'contrast'
                                    }>
                                    WPO | {a.name}
                                  </Badge>
                                </a>{' '}
                              </span>
                            ))}
                            <p style={{ fontSize: '0.7rem' }}>
                              Click badge for details and pricing
                            </p>
                            <p
                              style={{ fontWeight: 600 }}
                              className='text-primary'>
                              Need more? Gain access to all plugins with a
                              single purchase for{' '}
                              <a
                                href='https://www.wpo365.com/downloads/enterprise-access-pass/'
                                onClick={() => {
                                  ;(window as any).dataLayer =
                                    (window as any).dataLayer || []
                                  ;(window as any).dataLayer.push({
                                    event: 'advisorLink',
                                    product: 'ENTERPRISE ACCESS PASS',
                                  })
                                }}
                                target='_blank'
                                rel='noreferrer'>
                                20, 50, 100 or 200
                              </a>{' '}
                              different sites!
                            </p>
                          </div>
                        </>
                      )}
                    </Card.Body>
                  </Card>
                </div>
              </Carousel.Item>
            ))}
          </Carousel>
        </Col>
      </Row>
      <Row>
        <Col>
          {carouselIndex < questions.length - 1 ? (
            <div className='bg-light text-center p-4'>
              <p style={{ fontWeight: 600 }}>
                Plugin(s) recommended by the Advisor
              </p>
              {advise.map((a: IExtension, idx: number) => (
                <span key={`advise-${idx}`}>
                  <a
                    href={a.url}
                    onClick={() => {
                      ;(window as any).dataLayer =
                        (window as any).dataLayer || []
                      ;(window as any).dataLayer.push({
                        event: 'advisorLink',
                        product: a.name,
                      })
                    }}
                    target='_blank'
                    rel='noreferrer'>
                    <Badge
                      bg={
                        a.type === ExtensionType.free
                          ? 'secondary'
                          : a.type === ExtensionType.addon
                          ? 'primary'
                          : 'contrast'
                      }>
                      WPO | {a.name}
                    </Badge>
                  </a>{' '}
                </span>
              ))}
              <p style={{ fontSize: '0.7rem' }}>
                Click badge for details and pricing
              </p>
            </div>
          ) : (
            <div className='bg-light text-center p-4'>
              <p style={{ fontWeight: 600 }}>
                <a
                  href='https://www.wpo365.com/contact/'
                  onClick={() => {
                    ;(window as any).dataLayer = (window as any).dataLayer || []
                    ;(window as any).dataLayer.push({
                      event: 'advisorLink',
                      product: 'presales',
                    })
                  }}
                  target='_blank'
                  rel='noreferrer'>
                  Do you have a pre-sales question?
                </a>
              </p>
            </div>
          )}
        </Col>
      </Row>
    </Container>
  )
}

export default Advisor
